import React, { useEffect, useState, useMemo } from "react";
import { Select, Checkbox, Tag } from "antd";
import Button from "./Button";

export const ALL_PARTNERS = null;

/**
 * Multi-select dropdown for picking one, several, or all partners (customers / suppliers / etc).
 *
 * Selection model:
 *   value === null (ALL_PARTNERS) → "All" applied — backend should treat as no filter
 *   value === [id, id, ...]      → explicit subset (must be non-empty)
 *   value === undefined          → nothing applied yet (caller should treat as disabled query)
 *
 * The dropdown shows a master "All …" checkbox plus a Cancel / Apply footer; the
 * caller's onApply only fires when Apply is clicked.
 */
const PartnerMultiSelect = ({
  value,
  onApply,
  options = [],
  placeholder = "Select…",
  allLabel = "All",
  loading = false,
  style,
}) => {
  const valueAsArray = useMemo(() => {
    if (value === ALL_PARTNERS) return options.map((o) => o.id);
    if (Array.isArray(value)) return value;
    return [];
  }, [value, options]);

  const [open, setOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState(valueAsArray);

  useEffect(() => {
    if (!open) setPendingIds(valueAsArray);
  }, [valueAsArray, open]);

  const allChecked = options.length > 0 && pendingIds.length === options.length;
  const someChecked = pendingIds.length > 0 && pendingIds.length < options.length;

  const toggleAll = (e) => {
    setPendingIds(e.target.checked ? options.map((o) => o.id) : []);
  };

  const handleApply = () => {
    if (pendingIds.length === 0) return;
    onApply(pendingIds.length === options.length ? ALL_PARTNERS : pendingIds);
    setOpen(false);
  };

  const handleCancel = () => {
    setPendingIds(valueAsArray);
    setOpen(false);
  };

  const handleDropdownVisibleChange = (next) => {
    if (next) setPendingIds(valueAsArray);
    setOpen(next);
  };

  // When `allChecked` is true we want exactly one "All …" chip in the input. We
  // achieve this with two layers of antd plumbing that need to agree:
  //   1. `maxTagCount={0}` hides every individual tagRender result.
  //   2. `maxTagPlaceholder` (below) emits the single "All …" chip.
  // tagRender still returns a sane Tag for the non-`allChecked` path, where we
  // do want the actual per-partner code/name chips up to `maxTagCount`.
  const tagRender = (props) => {
    const { value: v, closable, onClose } = props;
    if (allChecked) return null;
    const opt = options.find((o) => o.id === v);
    if (!opt) return null;
    return (
      <Tag closable={closable} onClose={onClose} style={{ marginInlineEnd: 4 }}>
        {opt.code} - {opt.name}
      </Tag>
    );
  };

  return (
    <Select
      mode="multiple"
      value={pendingIds}
      onChange={setPendingIds}
      open={open}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      loading={loading}
      placeholder={placeholder}
      style={style}
      showSearch
      maxTagCount={allChecked ? 0 : 2}
      maxTagPlaceholder={(omitted) =>
        allChecked ? (
          // Single "All Customers" chip — the count is intentionally omitted
          // because the page-level banner already reports the meaningful number
          // (sections with activity) and showing two different totals here
          // ("All Customers (84)" vs banner "55 with activity") confused users.
          <Tag color="blue" style={{ marginInlineEnd: 0 }}>
            {allLabel}
          </Tag>
        ) : (
          `+${omitted.length} more`
        )
      }
      tagRender={tagRender}
      filterOption={(input, option) => {
        const opt = options.find((o) => o.id === option.value);
        if (!opt) return false;
        const t = input.toLowerCase();
        return (
          opt.code.toLowerCase().includes(t) ||
          opt.name.toLowerCase().includes(t)
        );
      }}
      options={options.map((o) => ({
        value: o.id,
        label: `${o.code} - ${o.name}`,
      }))}
      dropdownRender={(menu) => (
        <div>
          <div
            style={{
              padding: "8px 12px",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked}
              onChange={toggleAll}
              disabled={loading || options.length === 0}
            >
              <strong>{allLabel}</strong>
            </Checkbox>
          </div>
          {menu}
          <div
            style={{
              padding: "8px 12px",
              borderTop: "1px solid #f0f0f0",
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            <Button size="small" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              size="small"
              type="primary"
              disabled={pendingIds.length === 0}
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    />
  );
};

export default PartnerMultiSelect;
